import HttpService from "../../services/HttpService";
import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Grid, Dialog, DialogTitle, DialogContent, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RenderOnRole from "../../components/RenderOnRole";
import EmployerSidebar from "./components/EmployerSidebar";
import SubDepartmentList from "./components/SubDepartmentList";
import CardStatsElement from "../../components/CardStatsElement";
import Movements from "../../components/Movements";
import CircularProgress from "@mui/material/CircularProgress";
import Labels from "../../labels/Labels";


import { useChosenEmployer } from "../../contexts/EmployerContext";

export default function QueryMultiBalancev2() {
  const queryClient = useQueryClient();
  const { chosenEmployerId } = useChosenEmployer();

  // Fetch multi-balance data
  async function fetchMultiBalanceStats() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/balance/multistats`
    );
    return response;
  }

  const {
    data: dataStats,
    isLoading: isLoadingStats,
    isError: isErrorStats,
  } = useQuery(["multiBalanceStats"], fetchMultiBalanceStats);

  // State for selected department and view type
  const [singleBalanceEmployerId, setSingleBalanceEmployerId] = useState(null);
  const [showType, setShowType] = useState(null);

  // Identify employer and subdepartments
  const { employerData, subDepartments } = useMemo(() => {
    if (!dataStats?.data) {
      return { employerData: null, subDepartments: [] };
    }

    // Get employer ID from context (preferred) or headers
    const headerEmployerId = HttpService.getAxiosClient().defaults.headers['x-chosen-employer'];
    
    // Prioritize context, then header. Ensure it's a string for comparison.
    const targetId = chosenEmployerId ? String(chosenEmployerId) : (headerEmployerId ? String(headerEmployerId) : null);
    
    // Find the employer balance - STRICT MATCH ONLY
    let employer = null;
    
    if (targetId && dataStats.data) {
      employer = dataStats.data.find(
        (item) => String(item.idEmployer) === targetId
      );
    }
    
    // Get subdepartments (all except the employer)
    const subs = employer 
      ? dataStats.data.filter((item) => String(item.idEmployer) !== String(employer.idEmployer))
      : [];

    return { employerData: employer, subDepartments: subs };
  }, [dataStats, chosenEmployerId]);

  // Fetch stats for the selected employer (used for the dialog)
  async function fetchBalanceStats() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/balance/stats?idEmployer=` + singleBalanceEmployerId
    );
    return response;
  }

  const {
    data: dataSingleStats,
    isLoading: isLoadingSingleStats,
    isError: isErrorSingleStats,
  } = useQuery(["balanceStats", singleBalanceEmployerId], fetchBalanceStats, {
    enabled: !!singleBalanceEmployerId && showType === "stats",
  });

  // Handlers for showing details
  const showBalanceDetails = (idEmployer) => {
    setSingleBalanceEmployerId(idEmployer);
    setShowType("stats");
  };

  const showMovements = (idEmployer) => {
    setSingleBalanceEmployerId(idEmployer);
    setShowType("movements");
    queryClient.invalidateQueries(["movements", "movementsCount"]);
  };

  const handleCloseDialog = () => {
    setSingleBalanceEmployerId(null);
    setShowType(null);
  };

  const isEmployerSelected = singleBalanceEmployerId === employerData?.idEmployer;

  return (
    <RenderOnRole roles={["pf-multibalance"]}>
      <Box sx={{ padding: "20px" }}>
        <Grid container spacing={3}>
          {/* Left Sidebar - Employer Info */}
          <Grid item xs={12} md={4} lg={3}>
            <EmployerSidebar
              employerData={employerData}
              subDepartments={subDepartments}
              isLoading={isLoadingStats || !employerData}
              isError={isErrorStats}
              onShowStats={showBalanceDetails}
              onShowMovements={showMovements}
              selected={isEmployerSelected}
            />
          </Grid>

          {/* Right Panel - Subdepartments List */}
          <Grid item xs={12} md={8} lg={9}>
            <SubDepartmentList
              subDepartments={subDepartments}
              isLoading={isLoadingStats}
              isError={isErrorStats}
              selectedDepartment={singleBalanceEmployerId}
              showType={showType}
              onShowStats={showBalanceDetails}
              onShowMovements={showMovements}
            />
          </Grid>
        </Grid>

        {/* Dialog for Employer Details */}
        <Dialog 
          open={isEmployerSelected} 
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
            <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" component="div" color="white">
                {employerData?.employerDescription} - {showType === "stats" ? Labels.getLabel("queryBalance.Statistics", "Statistics") : Labels.getLabel("queryBalance.Movements", "Movements")}
              </Typography>
              {isLoadingSingleStats && <CircularProgress sx={{ color: "white" }} size={20} />}
            </Box>
            <Box>
              <IconButton
                edge="end"
                color="inherit"
                size="small"
                onClick={handleCloseDialog}
                aria-label="close"
              >
                <CloseIcon sx={{ color: "white" }} />
              </IconButton>
            </Box>
          </Box>
          <DialogContent dividers>
            {showType === "stats" && (
              <Grid container spacing={2}>
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.CreditsThisMonth", "Credits this month")}
                  data={dataSingleStats && dataSingleStats.data.creditsThisMonth}
                  type="amount"
                />
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.CreditsThisWeek", "Credits this week")}
                  data={dataSingleStats && dataSingleStats.data.creditsThisWeek}
                  type="amount"
                />
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.CreditsToday", "Credits today")}
                  data={dataSingleStats && dataSingleStats.data.creditsToday}
                  type="amount"
                />
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.DebitsThisMonth", "Debits this month")}
                  data={dataSingleStats && dataSingleStats.data.debitsThisMonth}
                  type="amount"
                />
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.DebitsThisWeek", "Debits this week")}
                  data={dataSingleStats && dataSingleStats.data.debitsThisWeek}
                  type="amount"
                />
                <CardStatsElement
                  headerTypograhy="h7"
                  minWidth={10}
                  fontSize={20}
                  md={4}
                  isErrorStats={isErrorSingleStats}
                  isLoadingStats={isLoadingSingleStats}
                  text={Labels.getLabel("queryBalance.DebitsToday", "Debits today")}
                  data={dataSingleStats && dataSingleStats.data.debitsToday}
                  type="amount"
                />
              </Grid>
            )}
            {showType === "movements" && (
              <Movements
                title={Labels.getLabel("queryBalance.Movements", "Movements")}
                key={singleBalanceEmployerId}
                idEmployer={singleBalanceEmployerId}
                idBalance={singleBalanceEmployerId}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </RenderOnRole>
  );
}
